import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Loader, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import UserWithAccess from './components/UserWithAccess';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DataRoomMembersWithAccess,
  DataRoomNode,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import MymeTypeIcon from '../../../MymeTypeIcon';
import { getArrayLength, getIsNodeFolder, getNodeMimeType } from 'utils';
import { DATE_FORMAT_DEFAULT, getFormattedDate } from 'dateUtils';

import './UsersWithAccessModal.styles.scss';

const removeMemberWithId = (members: DataRoomMember[], memberId: string) =>
  members.filter((member) => member._id !== memberId);

const removeAccessForMember = (members: DataRoomMember[], memberId: string) =>
  members.map((member) =>
    member._id !== memberId ? member : { ...member, canRevoke: false },
  );

const getFileTypeLabel = (node: DataRoomNode) => {
  const { originalFileName, name: documentName } = node?.document || {};

  // A stored document name can be a key with no extension, so keep trying the next candidate.
  for (const candidate of [originalFileName, documentName, node?.name]) {
    const [, extension] = candidate?.match(/\.([^.\s]+)$/) || [];

    if (extension) return extension.toUpperCase();
  }

  return '';
};

type UsersWithAccessModalProps = {
  className?: string;
  node: DataRoomNode;
  decrementNumberOfMemberAccess: () => void;
} & ModalProps;

const UsersWithAccessModal: React.ForwardRefRenderFunction<
  ModalRef,
  UsersWithAccessModalProps
> = (props, ref) => {
  const { className, node, decrementNumberOfMemberAccess, ...rest } = props;

  const { _id: nodeId, name, modifiedAt } = node || {};

  const [membersWithAccess, setMembersWithAccess] =
    useState<DataRoomMembersWithAccess>();

  const { sellSideMembers = [], buySideMembers = [] } = membersWithAccess || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('users-with-access-modal', className);

  const { t } = useTranslation();

  const [getMembers, isLoading] = useCallbackAsync(async () => {
    const { data } = await dataRoomApi.nodes.getMembersWithAccess(nodeId);
    setMembersWithAccess(data);
  });

  // Falls back to the count already on the node so the header doesn't flash "(0)" while loading.
  const numberOfMembers = membersWithAccess
    ? getArrayLength(buySideMembers) + getArrayLength(sellSideMembers)
    : node?.numberOfMembersWithAccess;

  const nodeMeta = useMemo(() => {
    const typeLabel = getIsNodeFolder(node)
      ? t('folders', { count: 1 })
      : getFileTypeLabel(node);

    const modifiedLabel =
      modifiedAt &&
      `${t('modified')} ${getFormattedDate(
        new Date(modifiedAt),
        DATE_FORMAT_DEFAULT,
      )}`;

    return [typeLabel, modifiedLabel].filter(Boolean).join(' · ');
  }, [node, modifiedAt, t]);

  const removeMember = useCallback(
    (memberId: string, hasAccessToOwnFiles: boolean) => {
      if (!hasAccessToOwnFiles) decrementNumberOfMemberAccess();

      const action = hasAccessToOwnFiles
        ? removeAccessForMember
        : removeMemberWithId;

      setMembersWithAccess((prev) => ({
        ...prev,
        sellSideMembers: action(prev.sellSideMembers, memberId),
        buySideMembers: action(prev.buySideMembers, memberId),
      }));
    },
    [decrementNumberOfMemberAccess],
  );

  const renderSection = useCallback(
    (title: string, members: Partial<DataRoomMember>[]) => {
      if (!members.length) return null;

      return (
        <section>
          <p className="section-title">
            {title} <span className="count">({getArrayLength(members)})</span>
          </p>
          {members.map((member) => (
            <UserWithAccess
              key={member._id}
              member={member}
              nodeId={nodeId}
              onRevokeAccessSuccess={() =>
                removeMember(member._id, member.hasAccessToOwnFiles)
              }
            />
          ))}
        </section>
      );
    },
    [nodeId, removeMember],
  );

  useEffect(() => {
    if (modal?.openState) {
      getMembers();
    }
  }, [getMembers, modal?.openState]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={
        <div className="modal-title">
          <p className="modal-title__heading">
            {t('membersWithAccess')}{' '}
            <span className="count">({numberOfMembers})</span>
          </p>

          <div className="modal-title__node">
            <MymeTypeIcon mimeType={getNodeMimeType(node)} />

            <div>
              <p className="modal-title__node-name">{name}</p>
              <p className="modal-title__node-meta">{nodeMeta}</p>
            </div>
          </div>
        </div>
      }
      {...rest}
    >
      {isLoading && <Loader />}

      {renderSection(t('buySide'), buySideMembers)}
      {renderSection(t('sellSide'), sellSideMembers)}
    </Modal>
  );
};

export default forwardRef(UsersWithAccessModal);
